import React, { useState, useEffect } from "react";
import BrandLogo from "../components/common/BrandLogo";
import { useParams, useNavigate } from "react-router-dom";
import { useUser, SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import { Ticket, MapPin, Info, LayoutDashboard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Typography } from "@mui/material";
import ModernLoader from "../components/common/ModernLoader";
import CustomSeatLayout from "../components/booking/CustomSeatLayout";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { toast } from "react-toastify";
import "./BookingPage.css";

const BookingPage = () => {
  const { showId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { user: clerkUser } = useUser();
  const [show, setShow] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [bookedSeats, setBookedSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seatLayout, setSeatLayout] = useState([]);
  const [conflictMessage, setConflictMessage] = useState("");
  const [lockedSeats, setLockedSeats] = useState([]);
  const [seatTimer, setSeatTimer] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [pendingSeats, setPendingSeats] = useState(new Set());
  const [processingSeatIds, setProcessingSeatIds] = useState(new Set());
  const [networkError, setNetworkError] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const showRef = React.useRef(null);

  // Sync ref with state
  useEffect(() => {
    showRef.current = show;
  }, [show]);

  useEffect(() => {
    fetchShowDetails();
    setupRealTimeListeners();

    return () => {
      if (selectedSeats.length > 0) {
        releaseSelectedSeats();
      }
      setProcessingSeatIds(new Set());
    };
  }, [showId]);

  // Add event listener for auto-released seats
  useEffect(() => {
    const handleAutoReleasedSeats = (event) => {
      const {
        showId: eventShowId,
        seats,
        userId: eventUserId,
        message,
      } = event.detail;
      if (eventShowId === showId && eventUserId === user?.id) {
        // Update selected seats if they were auto-released
        setSelectedSeats((prev) =>
          prev.filter((seat) => !seats.includes(seat.id)),
        );
        if (seats.length > 0 && message) {
          toast.warning(message);
        }
      }
    };

    window.addEventListener("seats-auto-released", handleAutoReleasedSeats);

    return () => {
      window.removeEventListener(
        "seats-auto-released",
        handleAutoReleasedSeats,
      );
    };
  }, [showId, user]);

  // Start seat selection timer
  const startSeatTimer = () => {
    if (seatTimer) clearInterval(seatTimer);

    setTimeRemaining(60);
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setSeatTimer(timer);
  };

  // Clear timer when seats are released or component unmounts
  useEffect(() => {
    if (selectedSeats.length === 0 && seatTimer) {
      clearInterval(seatTimer);
      setSeatTimer(null);
      setTimeRemaining(0);
    }

    return () => {
      if (seatTimer) clearInterval(seatTimer);
    };
  }, [selectedSeats.length, seatTimer]);

  // Clear pending seats after timeout
  useEffect(() => {
    if (pendingSeats.size > 0) {
      const timeout = setTimeout(() => {
        setPendingSeats(new Set());
      }, 3000); // Clear pending after 3 seconds

      return () => clearTimeout(timeout);
    }
  }, [pendingSeats.size]);

  const setupRealTimeListeners = () => {
    window.addEventListener("seat-conflict", handleSeatConflict);
    window.addEventListener("seats-selected", handleSeatsSelected);
    window.addEventListener("seats-locked", handleSeatsLocked);
    window.addEventListener("seats-released", handleSeatsReleased);
    window.addEventListener("seats-booked", handleSeatsBooked);
    window.addEventListener("seats-auto-released", handleAutoReleasedSeats);
    window.addEventListener("seats-available", handleSeatsAvailable);
    window.addEventListener("seat-layout-updated", handleSeatLayoutUpdated);
    window.addEventListener("show-deleted", handleShowDeleted);
    window.addEventListener("show-updated", handleShowUpdated);
    window.addEventListener("theater-updated", handleTheaterUpdated);
    window.addEventListener("screen-updated", handleScreenUpdated);

    return () => {
      window.removeEventListener("seat-conflict", handleSeatConflict);
      window.removeEventListener("seats-selected", handleSeatsSelected);
      window.removeEventListener("seats-locked", handleSeatsLocked);
      window.removeEventListener("seats-released", handleSeatsReleased);
      window.removeEventListener("seats-booked", handleSeatsBooked);
      window.removeEventListener(
        "seats-auto-released",
        handleAutoReleasedSeats,
      );
      window.removeEventListener("seats-available", handleSeatsAvailable);
      window.removeEventListener("seat-layout-updated", handleSeatLayoutUpdated);
      window.removeEventListener("show-deleted", handleShowDeleted);
      window.removeEventListener("show-updated", handleShowUpdated);
      window.removeEventListener("theater-updated", handleTheaterUpdated);
      window.removeEventListener("screen-updated", handleScreenUpdated);
    };
  };

  const handleSeatConflict = (event) => {
    const {
      showId: conflictShowId,
      conflicts,
      message,
      userId: conflictUserId,
    } = event.detail;
    // Only show conflict message if it's for the current show and meant for this user
    if (
      conflictShowId === showId &&
      (!conflictUserId || conflictUserId === user?.id)
    ) {
      setConflictMessage(message);
      toast.error(message);
      setTimeout(() => setConflictMessage(""), 5000);
    }
  };

  const handleSeatsSelected = (event) => {
    const { showId: eventShowId, seats, userId: eventUserId } = event.detail;
    // Only process if it's for current show and NOT from current user
    if (
      eventShowId === showId &&
      eventUserId !== user?.id &&
      eventUserId !== clerkUser?.id
    ) {
      console.log("Other user selected seats:", seats, "by user:", eventUserId);

      // Add to locked seats (seats selected by others)
      setLockedSeats((prev) => {
        const newLocked = [...new Set([...prev, ...seats])];
        return newLocked;
      });

      // Remove from our selected seats if we had them selected (conflict resolution)
      setSelectedSeats((prev) => {
        const filtered = prev.filter((seat) => !seats.includes(seat.id));
        if (filtered.length !== prev.length) {
          toast.warning(
            "Some of your selected seats were taken by another user",
          );
        }
        return filtered;
      });
    }
  };

  const handleSeatsLocked = (event) => {
    const { showId: eventShowId, seats, userId: eventUserId } = event.detail;
    // Only process if it's for current show and NOT from current user
    if (
      eventShowId === showId &&
      eventUserId !== user?.id &&
      eventUserId !== clerkUser?.id
    ) {
      console.log(
        "Seats locked by other user:",
        seats,
        "by user:",
        eventUserId,
      );
      setLockedSeats((prev) => [...new Set([...prev, ...seats])]);
    }
  };

  const handleSeatsReleased = (event) => {
    const {
      showId: eventShowId,
      seats,
      userId: eventUserId,
      reason,
    } = event.detail;
    if (eventShowId === showId) {
      // Always clear from locked seats for all users
      setLockedSeats((prev) => prev.filter((seat) => !seats.includes(seat)));

      // Clear from pending seats
      setPendingSeats((prev) => {
        const newSet = new Set(prev);
        seats.forEach((seat) => newSet.delete(seat));
        return newSet;
      });

      // Clear from selected seats for ALL users when expired or if it's this user's seats
      if (
        reason === "expired" ||
        eventUserId === user?.id ||
        eventUserId === clerkUser?.id
      ) {
        setSelectedSeats((prev) =>
          prev.filter((seat) => !seats.includes(seat.id)),
        );

        // Clear timer if seats expired
        if (reason === "expired" && seatTimer) {
          clearInterval(seatTimer);
          setSeatTimer(null);
          setTimeRemaining(0);
        }
      }
    }
  };

  const handleSeatsBooked = (event) => {
    const { showId: eventShowId, bookedSeats: newBookedSeats } = event.detail;
    if (eventShowId === showId) {
      setBookedSeats((prev) => [...new Set([...prev, ...newBookedSeats])]);
      setLockedSeats((prev) =>
        prev.filter((seat) => !newBookedSeats.includes(seat)),
      );
    }
  };

  const handleAutoReleasedSeats = (event) => {
    const {
      showId: eventShowId,
      seats,
      userId: eventUserId,
      message,
    } = event.detail;
    if (
      eventShowId === showId &&
      (eventUserId === user?.id || eventUserId === clerkUser?.id)
    ) {
      // Clear selected seats for this user
      setSelectedSeats((prev) =>
        prev.filter((seat) => !seats.includes(seat.id)),
      );

      // Clear timer
      if (seatTimer) {
        clearInterval(seatTimer);
        setSeatTimer(null);
        setTimeRemaining(0);
      }

      // Show message
      if (seats.length > 0 && message) {
        toast.warning(message);
      }
    }

    // Always remove from locked seats for all users
    if (eventShowId === showId) {
      setLockedSeats((prev) => prev.filter((seat) => !seats.includes(seat)));
    }
  };

  const handleSeatsAvailable = (event) => {
    const { showId: eventShowId, seats } = event.detail;
    if (eventShowId === showId) {
      // Remove from locked seats when they become available
      setLockedSeats((prev) => prev.filter((seat) => !seats.includes(seat)));
    }
  };

  const handleSeatLayoutUpdated = (event) => {
    const { theaterId: eventTheaterId, screenId: eventScreenId, seatLayout: newLayout } = event.detail;
    const currentShow = showRef.current;

    // Only refresh if the update belongs to the current theater and screen
    if (currentShow?.theater?._id === eventTheaterId) {
      // If we have screenId, verify it matches
      if (!eventScreenId || currentShow?.theater?.screens?.some(s => s._id === eventScreenId && s.screenNumber === currentShow.screenNumber)) {
        console.log("Theater layout updated, refreshing show details...");
        fetchShowDetails(true);
        toast.info("The auditorium layout was updated.");
      }
    }
  };

  const handleShowDeleted = (event) => {
    const { id } = event.detail;
    if (id === showId) {
      toast.error("This show has been cancelled or removed by the theater admin.");
      setTimeout(() => navigate("/movies"), 2000);
    }
  };

  const handleShowUpdated = (event) => {
    const showData = event.detail;
    if (showData._id === showId) {
      console.log("Show updated live, refreshing...");

      if (showData.isActive === false) {
        toast.error("This show is no longer available for booking.");
        setTimeout(() => navigate("/movies"), 2000);
        return;
      }

      setShow(prev => ({ ...prev, ...showData }));
      if (showData.pricing) {
        toast.info("Show pricing or schedule has been updated.");
      }
    }
  };

  const handleTheaterUpdated = (event) => {
    const theaterData = event.detail;
    if (theaterData._id === showRef.current?.theater?._id) {
      fetchShowDetails(true);
      toast.info("The auditorium layout was updated.");
    }
  };

  const handleScreenUpdated = (event) => {
    const { theaterId: eventTheaterId, screen } = event.detail;
    if (!screen) return; // Guard clause
    const currentShow = showRef.current;
    if (currentShow?.theater?._id === eventTheaterId && screen.screenNumber === currentShow.screenNumber) {
      console.log("Screen updated live, refreshing layout...");
      fetchShowDetails(true);
      toast.info("Auditorium configuration was updated.");
    }
  };

  const selectSeatsOnServer = async (seats) => {
    try {
      const response = await api.post("/bookings/select-seats", {
        showId: show._id,
        seats: seats.map((seat) => seat.id),
      });
      setNetworkError(false);
      return { success: true };
    } catch (error) {
      if (
        error.code === "ERR_NETWORK" ||
        !navigator.onLine ||
        error.response?.status === 503
      ) {
        setNetworkError(true);
        const message =
          error.response?.data?.type === "network_error"
            ? "Database connection lost. Please try again."
            : "Network connection lost. Please check your internet connection.";
        toast.error(message);
        return { success: false, message: "Network error" };
      }
      if (error.response?.status === 409) {
        const message = error.response.data.message;
        const conflicts = error.response.data.conflicts || [];
        toast.error(message);
        setConflictMessage(message);
        setTimeout(() => setConflictMessage(""), 5000);
        setLockedSeats((prev) => [...new Set([...prev, ...conflicts])]);
        return { success: false, message, conflicts };
      }
      toast.error("Server error. Please try again.");
      return { success: false, message: "Failed to select seats" };
    }
  };

  const releaseSelectedSeats = async () => {
    if (selectedSeats.length === 0) return;

    try {
      await api.post("/bookings/release-seats", {
        showId: show._id,
        seats: selectedSeats.map((seat) => seat.id),
      });
    } catch (error) {
      console.error("Error releasing seats:", error);
    }
  };

  const fetchShowDetails = async (isSync = false) => {
    if (!isSync) setLoading(true);

    try {
      const response = await api.get(`/shows/${showId}`);
      const showData = response.data;

      if (!showData.isActive) {
        toast.error("This show is no longer available.");
        navigate("/movies");
        return;
      }

      setShow(showData);
      setBookedSeats(showData.bookedSeats || []);
      generateSeatLayout(showData.theater, showData.screenNumber);
      setNetworkError(false);
    } catch (error) {
      if (
        error.code === "ERR_NETWORK" ||
        !navigator.onLine ||
        error.response?.status === 503
      ) {
        setNetworkError(true);
        const message =
          error.response?.data?.type === "network_error"
            ? "Database connection lost. Unable to load show details."
            : "Network connection lost. Unable to load show details.";
        toast.error(message);
      } else {
        toast.error("Failed to load show details");
        navigate("/movies");
      }
    } finally {
      setLoading(false);
    }
  };

  const generateSeatLayout = (theater, screenNumber = 1) => {
    if (theater?.screens && theater.screens.length > 0) {
      const screen = theater.screens.find(
        (s) => s.screenNumber === screenNumber,
      );

      if (screen?.seatLayout && screen.seatLayout.length > 0) {
        setSeatLayout(screen.seatLayout);
        return;
      }
    }

    // Exact layout logic and tiers matching district reference:
    const defaultLayout = [
      {
        row: "R",
        seats: [null, null, null, null, null, null, 1, 2, 3, 4, null, 5, 6, 7, 8, null, null, null, null, null, null],
        class: "prime-plus",
        section: "PRIME PLUS ROWS",
        price: 410,
      },
      ...["Q", "P", "N", "M", "L", "K", "J"].map((r, i) => ({
        row: r,
        seats: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, null, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
        class: "prime-plus",
        section: "PRIME PLUS ROWS",
        price: 410,
      })),
      {
        row: "H",
        seats: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, null, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
        class: "extra-legroom",
        section: "EXTRA LEGROOM ROWS",
        price: 440,
      },
      ...["G", "F", "E", "D"].map(r => ({
        row: r,
        seats: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, null, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
        class: "prime",
        section: "PRIME ROWS",
        price: 320,
      })),
      ...["C", "B"].map(r => ({
        row: r,
        seats: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, null, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21],
        class: "classic",
        section: "CLASSIC ROWS",
        price: 290,
      })),
      {
        row: "A",
        seats: [1, 2, 3, 4, 5, 6, 7, 8, 1000, 1001, null, 1002, 1003, 1004, 14, 15, 16, 17, 18, 19, 20, 21],
        class: "classic",
        section: "CLASSIC ROWS",
        price: 290,
      }
    ];
    setSeatLayout(defaultLayout);
  };

  const handleSeatClick = async (seat) => {
    if (bookedSeats.includes(seat.id)) return;
    if (pendingSeats.has(seat.id)) return;
    if (processingSeatIds.has(seat.id)) return; // Prevent simultaneous operations

    if (
      lockedSeats.includes(seat.id) &&
      !selectedSeats.some((s) => s.id === seat.id) &&
      !pendingSeats.has(seat.id)
    ) {
      const conflictMessage = `Seat ${seat.id} is currently being selected by another user`;
      toast.error(conflictMessage);
      setConflictMessage(conflictMessage);
      setTimeout(() => setConflictMessage(""), 5000);
      return;
    }

    await processSeatClick(seat);
  };

  const processSeatClick = async (seat) => {
    // Mark seat as being processed
    setProcessingSeatIds((prev) => new Set([...prev, seat.id]));

    try {
      const isSelected = selectedSeats.some((s) => s.id === seat.id);

      if (isSelected) {
        // Deselecting a seat
        setPendingSeats((prev) => new Set([...prev, seat.id]));

        try {
          await api.post("/bookings/release-seats", {
            showId: show._id,
            seats: [seat.id],
          });

          // Only update UI after successful server response
          setSelectedSeats((prev) => prev.filter((s) => s.id !== seat.id));
          setNetworkError(false);
        } catch (error) {
          if (
            error.code === "ERR_NETWORK" ||
            !navigator.onLine ||
            error.response?.status === 503
          ) {
            setNetworkError(true);
            const message =
              error.response?.data?.type === "network_error"
                ? "Database connection lost. Seat may not be released properly."
                : "Network error. Seat may not be released properly.";
            toast.error(message);
          } else {
            toast.error("Failed to release seat. Please try again.");
          }
          // Don't change selected seats on error
        } finally {
          setPendingSeats((prev) => {
            const newSet = new Set(prev);
            newSet.delete(seat.id);
            return newSet;
          });
        }
      } else {
        // Selecting a new seat
        if (selectedSeats.length + pendingSeats.size >= 10) {
          toast.warning("Maximum 10 seats can be selected");
          return;
        }

        setPendingSeats((prev) => new Set([...prev, seat.id]));

        try {
          const result = await selectSeatsOnServer([seat]);
          if (result.success) {
            // Only update UI after successful server response
            setSelectedSeats((prev) => [...prev, seat]);
            startSeatTimer();
          } else {
            // Handle conflicts or errors
            if (result.conflicts && result.conflicts.length > 0) {
              setLockedSeats((prev) => [
                ...new Set([...prev, ...result.conflicts]),
              ]);
            }
          }
        } finally {
          setPendingSeats((prev) => {
            const newSet = new Set(prev);
            newSet.delete(seat.id);
            return newSet;
          });
        }
      }
    } finally {
      // Always remove from processing set
      setProcessingSeatIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(seat.id);
        return newSet;
      });
    }
  };

  const getSeatStatus = (seat) => {
    if (bookedSeats.includes(seat.id)) return "booked";
    if (pendingSeats.has(seat.id)) return "pending";
    if (selectedSeats.find((s) => s.id === seat.id)) return "selected";
    if (lockedSeats.includes(seat.id) && !pendingSeats.has(seat.id))
      return "locked";
    return "available";
  };

  const calculateTotal = () => {
    return selectedSeats.reduce((total, seat) => total + seat.price, 0);
  };

  const handlePaymentClick = async () => {
    if (!clerkUser) {
      navigate("/sign-in");
      return;
    }

    setPaymentLoading(true);
    try {
      const bookingData = {
        bookingId: `BK${Date.now()}`,
        movieTitle: show.movie?.title,
        moviePoster: show.movie?.poster,
        theaterName: show.theater?.name,
        showDate: formatDate(show.showDate),
        showTime: formatTime(show.showTime),
        seats: selectedSeats.map((seat) => seat.id),
        totalAmount: calculateTotal(),
        showId: show._id,
        seatDetails: selectedSeats,
        userEmail:
          clerkUser?.primaryEmailAddress?.emailAddress ||
          "customer@example.com",
        genre: show.movie?.genre?.[0] || "Movie",
        rating: show.movie?.rating || "8.5",
        clerkId: clerkUser?.id,
        userId: user?.id || user?._id || clerkUser?.id,
      };

      localStorage.setItem("bookingConfirmation", JSON.stringify(bookingData));

      const response = await api.post("/payment/create-checkout-session", {
        amount: bookingData.totalAmount,
        bookingId: bookingData.bookingId,
        userEmail: bookingData.userEmail,
        movieTitle: bookingData.movieTitle,
        seats: bookingData.seats,
        showId: bookingData.showId,
        seatDetails: bookingData.seatDetails,
        theaterName: bookingData.theaterName,
        showDate: bookingData.showDate,
        showTime: bookingData.showTime,
        moviePoster: bookingData.moviePoster,
        clerkId: clerkUser?.id,
        userId: user?.id || user?._id || clerkUser?.id,
      });

      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        setPaymentError("Payment setup failed. Please try again.");
        setPaymentLoading(false);
      }
    } catch (error) {
      console.error("Payment error:", error);
      const errorMsg =
        error.response?.data?.error || error.message || "Payment setup failed";
      setPaymentError(errorMsg);
      setPaymentLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (time) => {
    if (!time) return "";
    try {
      const [h, m] = time.split(":");
      const date = new Date();
      date.setHours(parseInt(h, 10) || 0, parseInt(m, 10) || 0, 0);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return time;
    }
  };

  const buildShowtimeSlots = () => {
    const rawTime = show?.showTime || "18:30";
    const [rawHours, rawMinutes] = rawTime.split(":");
    const hours = Number.parseInt(rawHours, 10);
    const minutes = Number.parseInt(rawMinutes, 10);
    const safeHours = Number.isFinite(hours) ? hours : 18;
    const safeMinutes = Number.isFinite(minutes) ? minutes : 30;

    const baseTime = new Date(2000, 0, 1, safeHours, safeMinutes, 0);
    const offsets = [0, 105, 390, 615, 675];

    return offsets.map((offset, index) => {
      const slotTime = new Date(baseTime);
      slotTime.setMinutes(slotTime.getMinutes() + offset);

      return {
        id: `${offset}-${index}`,
        time: slotTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
        active: index === 0,
      };
    });
  };

  if (loading) {
    return <ModernLoader text="Loading Show Details" />;
  }

  if (!show) {
    return (
      <div
        style={{
          padding: "100px 20px",
          textAlign: "center",
          backgroundColor: "#fff",
          minHeight: "100vh",
        }}
      >
        <h2 style={{ color: "#131316" }}>Show not found</h2>
        <button
          onClick={() => navigate("/movies")}
          style={{
            marginTop: "20px",
            padding: "10px 20px",
            backgroundColor: "#6D49FD",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          Back to Movies
        </button>
      </div>
    );
  }

  const showDateObject = new Date(show.showDate);
  const showWeekday = showDateObject.toLocaleDateString("en-US", {
    weekday: "short",
  });
  const showDayMonth = showDateObject.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
  });
  const subtitleDayMonth = showDateObject.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
  });

  const theaterLabel = show.theater?.name || "Cinema";
  const subtitleLine = `${theaterLabel} | ${subtitleDayMonth}, ${formatTime(show.showTime)}`;
  const showtimeSlots = buildShowtimeSlots();
  const selectedCount = selectedSeats.length;
  const totalAmount = calculateTotal();
  const totalAmountLabel = totalAmount.toLocaleString("en-IN");
  const selectedSeatList = selectedSeats.map((seat) => seat.id).join(", ");
  const userDisplayName =
    clerkUser?.firstName || user?.name || user?.username || "User";
  const userInitial = userDisplayName.charAt(0).toUpperCase();

  return (
    <div className="district-booking-page" style={{ backgroundColor: '#fff', color: 'rgb(19, 19, 22)', fontFamily: 'BeVietnamPro, sans-serif' }}>
      {loading && <ModernLoader />}

      <header className="district-booking-header">
        <div className="district-container header-inner">
          <div className="district-left">
            <button className="district-back-button" onClick={() => navigate("/movies")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
              </svg>
            </button>
            <div className="district-brand" onClick={() => navigate("/")}>
              <BrandLogo className="district-navbar-logo" />
            </div>
          </div>

          <div className="district-center">
            <h1 className="movie-title">{show.movie?.title}</h1>
            <p className="movie-meta">
              {showDayMonth}, {formatTime(show.showTime)} at {show.theater?.name}, {show.theater?.location || "Ahmedabad"}
            </p>
          </div>

          <div className="district-right">
            <SignedIn>
              <UserButton 
                appearance={{
                  elements: {
                    userButtonAvatarBox: "district-user-avatar",
                    userButtonTrigger: "district-user-trigger"
                  }
                }}
              >
                <UserButton.MenuItems>
                  <UserButton.Link
                    label="My Bookings"
                    href="/my-bookings"
                    labelIcon={<Ticket size={16} />}
                  />
                  <UserButton.Link
                    label="About"
                    href="/about"
                    labelIcon={<Info size={16} />}
                  />
                  {clerkUser?.publicMetadata?.role === "admin" && (
                    <UserButton.Link
                      label="Admin Dashboard"
                      href="/admin"
                      labelIcon={<LayoutDashboard size={16} />}
                    />
                  )}
                </UserButton.MenuItems>
              </UserButton>
            </SignedIn>
            <SignedOut>
              <div className="district-user-avatar" onClick={() => navigate("/sign-in")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                </svg>
              </div>
            </SignedOut>
          </div>
        </div>
      </header>

      <main className="district-seat-shell">
        <div className="district-showtime-strip">
          <div className="district-container strip-container">
            <div className="district-date-block">
              <span className="date-weekday">{showWeekday}</span>
              <span className="date-day">{showDayMonth}</span>
            </div>
            <div className="district-time-pills">
              {showtimeSlots.map((slot, index) => (
                <div key={slot.id} className={`district-time-card ${slot.active ? 'active' : ''}`}>
                  <span className="time-text">{slot.time}</span>
                  {index % 4 === 3 && <span className="tier-label">INSIGNIA</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {(networkError || conflictMessage || paymentError) && (
          <div className="district-alert-banner" role="alert" style={{ margin: '16px' }}>
            {networkError && <span>Network connection lost. Please check your internet.</span>}
            {conflictMessage && <span>{conflictMessage}</span>}
            {paymentError && <span>{paymentError}</span>}
          </div>
        )}

        <div className="district-seat-frame">
          <CustomSeatLayout
            seatLayout={seatLayout}
            bookedSeats={bookedSeats}
            selectedSeats={selectedSeats}
            lockedSeats={lockedSeats}
            pendingSeats={Array.from(pendingSeats)}
            onSeatClick={handleSeatClick}
            pricing={{
              silver: show?.pricing?.silver || show?.price || 150,
              gold: show?.pricing?.gold || Math.round((show?.price || 150) * 1.3),
              premium: show?.pricing?.premium || Math.round((show?.price || 150) * 1.8),
            }}
          />
        </div>
      </main>

      <AnimatePresence mode="wait">
        {selectedCount === 0 ? (
          <motion.footer
            key="default-footer"
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="district-booking-footer default-view"
          >
            <div className="district-footer-content-default">
              <div className="screen-indicator">
                <div className="screen-indicator-glow"></div>
                <span className="screen-indicator-text">SCREEN THIS WAY</span>
              </div>

              <div className="legend-row">
                <div className="legend-item">
                  <span className="legend-box best"></span>
                  <span className="legend-label">Best Seats</span>
                </div>
                <div className="legend-item">
                  <span className="legend-box available"></span>
                  <span className="legend-label">Available</span>
                </div>
                <div className="legend-item">
                  <span className="legend-box occupied">×</span>
                  <span className="legend-label">Occupied</span>
                </div>
                <div className="legend-item">
                  <span className="legend-box selected"></span>
                  <span className="legend-label">Selected</span>
                </div>
              </div>
            </div>
          </motion.footer>
        ) : (
          <motion.footer
            key="selection-footer"
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="district-booking-footer selection-view"
          >
            <div className="district-footer-inner">
              <div className="district-checkout-row">
                <div className="district-checkout-summary">
                  <div className="summary-main">
                    <span className="district-summary-value">
                      {selectedCount} Seat{selectedCount === 1 ? "" : "s"}
                    </span>
                    <span className="district-summary-amount">
                      ₹{totalAmountLabel}
                    </span>
                  </div>
                  <span className="district-summary-seats">
                    {selectedSeatList}
                  </span>
                  {timeRemaining > 0 && (
                    <span className="district-hold-badge">
                      {timeRemaining}s hold
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handlePaymentClick}
                  disabled={paymentLoading}
                  className="district-proceed-button"
                >
                  {paymentLoading ? "Processing..." : "Proceed"}
                </button>
              </div>
            </div>
          </motion.footer>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookingPage;
